import React from 'react'
import { Link } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { CreditCard, Plus, Pencil, AlertTriangle, ChevronRight, CircleAlert, CircleCheck, Loader2 } from 'lucide-react'

// PaymentMethodCard (FE2) — the card-on-file management surface. Shows the brand / last-4 /
// expiry of the saved card (read by the backend through the Gateway seam — this component
// never touches Stripe and does no money math), with an "Add card" / "Update card" action
// that opens the in-app SetupIntent PaymentElement modal. No card on file → the empty,
// add-a-card state.

export interface PaymentMethodSummary {
  last4: string | null
  brand: string | null
  expMonth: number | null
  expYear: number | null
}

/**
 * WHAT THE LAST "PAY" CLICK SAID, shown on the block it was about. `sentence` is the server's own
 * (Operator::BillingController#pay_payment_method), or, for `error` only, the page's one sentence
 * about a request that never reached a verdict. `tone` is chosen by the server for an answer
 * (`success` when nothing is left to pay, `attention` while something still is) and is `error` for
 * a refusal; this file maps it to a colour and nothing else.
 */
export interface PayResult {
  sentence: string
  tone: 'success' | 'attention' | 'error'
}

// Stripe's card brand is a lowercase token ("mastercard", "amex"). These are the names the
// networks are actually called on a statement — a presentation mapping and nothing else. An
// unknown token is title-cased rather than dropped: a brand we have not met still names itself.
const BRAND_LABELS: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'American Express',
  discover: 'Discover',
  diners: 'Diners Club',
  jcb: 'JCB',
  unionpay: 'UnionPay',
  eftpos_au: 'Eftpos',
}

function brandLabel(brand: string | null): string {
  if (!brand) return 'Card'
  return BRAND_LABELS[brand] ?? brand.charAt(0).toUpperCase() + brand.slice(1)
}

// A business with several legal entities settles each cluster of hotels on its own card
// (billing-payers §5). The SAME card surface renders both shapes: with no `title`/`hotels`/`alert`
// it is the single "Payment method" card this page has always had, and with them it is one block
// of the Payment methods list — one component, so two card surfaces can never drift apart.
//
// EVERY SENTENCE HERE IS SERVER-COMPOSED (Operator::BillingController#payer_alert_sentence). This
// component performs no date math, no money math and no state classification.
export default function PaymentMethodCard({
  paymentMethod,
  onManage,
  onEdit = null,
  title = 'Payment method',
  billingEmail = null,
  hotels = [],
  showHotels = false,
  alertState = null,
  alertSentence = null,
  alertInvoiceHref = null,
  payLabel = null,
  onPay = null,
  paying = false,
  payResult = null,
}: {
  paymentMethod: PaymentMethodSummary | null
  onManage: () => void
  /**
   * Edit this payment method's NAME and INVOICE EMAIL (billing-payers §9.1) — never its card,
   * which is vaulted through `onManage`. Null on a surface that does not offer the edit.
   */
  onEdit?: (() => void) | null
  title?: string
  billingEmail?: string | null
  hotels?: string[]
  /**
   * Whether this block should state which hotels it pays for. True on a business with several
   * payment methods, where "no hotels yet" is a real and important state — a method that has just
   * been added pays for nothing until the grouping is saved, and the block must say so rather
   * than look identical to one that pays for the whole portfolio.
   */
  showHotels?: boolean
  alertState?: string | null
  alertSentence?: string | null
  /**
   * The document the alert sentence NAMES, as a backend-served path. The sentence is one string
   * composed server-side and it stays one string — the row as a whole becomes the link rather than
   * this component cutting an invoice number out of a sentence it did not write.
   */
  alertInvoiceHref?: string | null
  /**
   * PAY WHAT'S DUE (billing two clocks, 2026-10-05). The button's own words, composed by the
   * backend with the TOTAL this payment method owes ("Pay £320.00 now"). Sent only when it owes
   * money AND has a card the charge can use; with no card, "Add card" above stays the ask.
   */
  payLabel?: string | null
  /** Ask the server to charge what is due. Null on a surface that offers no payment. */
  onPay?: (() => void) | null
  /** A payment for this block is in flight: the button is disabled so a click cannot repeat it. */
  paying?: boolean
  /** The answer to the last payment asked for here, rendered verbatim. */
  payResult?: PayResult | null
}) {
  const hasCard = !!paymentMethod && !!paymentMethod.last4
  const canPay = !!onPay && !!payLabel

  return (
    <div className="overflow-hidden rounded-xl border border-border/60 bg-white shadow-sm">
      <div className="flex items-center justify-between gap-3 border-b border-border/40 px-5 py-4">
        <div className="flex min-w-0 items-center gap-2">
          <CreditCard className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="truncate text-sm font-semibold text-foreground">{title}</span>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {onEdit && (
            <Button
              onClick={onEdit}
              variant="ghost"
              size="sm"
              aria-label={`Edit ${title}`}
              title={`Edit ${title}`}
              className="h-8 w-8 rounded-lg p-0"
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          )}
          <Button onClick={onManage} variant="outline" size="sm" className="h-8 rounded-lg">
            {hasCard ? (
              <>
                <CreditCard className="mr-1.5 h-3.5 w-3.5" />
                Update card
              </>
            ) : (
              <>
                <Plus className="mr-1.5 h-3.5 w-3.5" />
                Add card
              </>
            )}
          </Button>
        </div>
      </div>

      <div className="px-5 py-4">
        {hasCard ? (
          // THE CHIP SIZES TO ITS LABEL. It was a fixed 48px box, which fits "VISA" and nothing
          // else: the first real production card was a Mastercard and "MASTERCARD" spilled across
          // the row (billing-payers §11.2). Height, border and mono-caps are unchanged — only the
          // width is now the label's, and the row wraps rather than colliding on a narrow card.
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <span className="inline-flex h-9 shrink-0 items-center whitespace-nowrap rounded-md border border-border/60 bg-muted px-2 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
              {brandLabel(paymentMethod!.brand)}
            </span>
            <div className="min-w-0 leading-tight">
              <p className="whitespace-nowrap text-sm font-medium tabular-nums text-foreground">
                •••• •••• •••• {paymentMethod!.last4}
              </p>
              {paymentMethod!.expMonth && paymentMethod!.expYear && (
                <p className="mt-0.5 whitespace-nowrap text-xs tabular-nums text-muted-foreground">
                  Expires {String(paymentMethod!.expMonth).padStart(2, '0')}/{paymentMethod!.expYear}
                </p>
              )}
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            No card on file. Add a card to keep your subscription active.
          </p>
        )}

        {billingEmail && (
          <p className="mt-3 text-xs text-muted-foreground">
            Invoices and payment notices are also sent to <span className="text-foreground">{billingEmail}</span>.
          </p>
        )}

        {hotels.length > 0 ? (
          <p className="mt-2 text-xs text-muted-foreground">
            Pays for <span className="text-foreground">{hotels.join(', ')}</span>.
          </p>
        ) : (
          showHotels && <p className="mt-2 text-xs text-muted-foreground">No hotels yet.</p>
        )}

        {alertSentence && (
          <AlertRow tone={alertState} href={alertInvoiceHref}>
            {alertSentence}
          </AlertRow>
        )}
      </div>

      {/* THE SETTLE-UP BAND. The one place on the block where money moves, so it sits apart from
          the card's details on its own tinted footer, with the page's single primary action in it.
          It stays after a payment that cleared everything, to say so, and leaves with the next
          page load. */}
      {(canPay || payResult) && (
        <div className="space-y-3 border-t border-border/40 bg-slate-50/70 px-5 py-3.5">
          {payResult && <PayResultLine result={payResult} />}
          {canPay && (
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
              <p className="text-xs text-muted-foreground">
                Charges the card above for everything due, oldest invoice first.
              </p>
              <Button
                onClick={onPay!}
                disabled={paying}
                aria-busy={paying || undefined}
                size="sm"
                className="h-9 shrink-0 rounded-lg px-4 font-semibold tabular-nums shadow-sm"
              >
                {paying && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
                {payLabel}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// The answer to a payment, in the server's words. Colour is the only thing decided here: emerald
// when nothing is left to pay, amber while something still is, rose when the request itself failed.
const RESULT_TONES: Record<PayResult['tone'], { box: string; Icon: typeof CircleCheck }> = {
  success: { box: 'border-emerald-200 bg-emerald-50 text-emerald-800', Icon: CircleCheck },
  attention: { box: 'border-amber-200 bg-amber-50 text-amber-800', Icon: CircleAlert },
  error: { box: 'border-rose-200 bg-rose-50 text-rose-700', Icon: CircleAlert },
}

function PayResultLine({ result }: { result: PayResult }) {
  const tone = RESULT_TONES[result.tone] ?? RESULT_TONES.attention
  const { Icon } = tone

  return (
    <div
      role={result.tone === 'error' ? 'alert' : 'status'}
      data-pay-result={result.tone}
      className={`animate-slide-in flex items-start gap-2 rounded-lg border px-3 py-2 text-xs font-medium ${tone.box}`}
    >
      <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span>{result.sentence}</span>
    </div>
  )
}

// THE DEADLINE ROW, AND THE DOCUMENT BEHIND IT. The sentence names an invoice number, so the row
// leads to that invoice when the backend served its path — a quiet affordance (a chevron, and the
// sentence underlining on hover), never a rewritten sentence. Navigation is an Inertia <Link>,
// never a button.
function AlertRow({
  tone,
  href,
  children,
}: {
  tone: string | null
  href: string | null
  children: React.ReactNode
}) {
  const palette =
    tone === 'blocked'
      ? 'border-rose-200 bg-rose-50 text-rose-800'
      : 'border-amber-200 bg-amber-50 text-amber-800'
  const body = (
    <>
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <span className={href ? 'group-hover:underline' : undefined}>{children}</span>
      {href && <ChevronRight className="ml-auto mt-0.5 h-3.5 w-3.5 shrink-0 opacity-60" />}
    </>
  )
  const className = `mt-3 flex items-start gap-2 rounded-lg border px-3 py-2 text-xs ${palette}`

  if (!href) return <div className={className}>{body}</div>

  return (
    <Link href={href} className={`group ${className} transition-colors hover:brightness-[0.98]`}>
      {body}
    </Link>
  )
}
